/**
 * LA PALETA — lo que se marca de pie en cada recinto (escalón E2, 2026-08-22).
 *
 * Primera capa: eléctrico. Dos niveles deliberados (plan de diseño §2 y §4):
 *   · lo OBSERVABLE lo marca el vendedor no técnico: cuenta contactos, apagadores,
 *     salidas de luz, aparatos que consumen; y señala hallazgos que se ven, se
 *     huelen o se tocan —olor a quemado, pastilla que salta, contacto flojo o
 *     caliente, dos patas, cable expuesto, empalme a la vista— SIEMPRE con foto;
 *   · lo que exige abrir el tablero (neutro unido a tierra, pastilla sobre calibre,
 *     aislamiento) NO está aquí: nace en la Revisión, que la hace el ingeniero.
 *
 * Cada toque escribe de inmediato un objeto de la cotización:
 *   cotizaciones.objetos[id] = { recinto, rubro, tipo, estado, q, detalle?, fotos?, nota?, capturadoEn }
 * con id DETERMINISTA para los contadores (`recinto__tipo__estado`), de modo que
 * dos toques nunca duplican y dos dispositivos se fusionan por id (E0); los
 * hallazgos llevan id propio porque son uno por hallazgo.
 *
 * Existente / Propuesto es un conmutador, no dos listas: en la misma recámara se
 * cuenta lo que hay y se marca lo que se va a poner, y la calculadora (E4) lee
 * las dos cosas. La plantilla por tipo de recinto propone lo típico (la recámara
 * de `pk1`: 4 contactos y 2 salidas de luz) y se corrige, no se captura de cero.
 *
 * Las fotos de evidencia van a 1200 px (en un contacto quemado o en la placa de
 * un aparato, 420 px no dejan leer nada) como documento propio en `fotos`, bajo
 * el tope de 900 KB; pasan a Storage cuando exista el bucket (decisión de Juan).
 */

import { useEffect, useState } from 'react'
import * as store from '../datos/store.js'
import { APARATOS } from '../motor/electrico.js'

export const TIPOS_ELECTRICO = [
  { id: 'contacto',      nombre: 'Contacto',               sku: 'SAL-CTO' },
  { id: 'contacto-agua', nombre: 'Contacto junto al agua', sku: 'SAL-CTO-P' },
  { id: 'apagador',      nombre: 'Apagador',               sku: null },
  { id: 'salida-luz',    nombre: 'Salida de luz',          sku: 'SAL-ILU' },
  { id: 'escalera',      nombre: 'Apagador de escalera',   sku: 'SAL-ESC' }
]

/** Hallazgos observables. El nombre es lo que el vendedor ve, no el diagnóstico. */
export const HALLAZGOS = [
  'Olor a quemado', 'Pastilla que salta', 'Contacto flojo', 'Contacto caliente al tacto',
  'Contacto de dos patas', 'Cable expuesto', 'Empalme a la vista'
]

/** Lo típico por tipo de recinto, en PROPUESTO. Cantidades del catálogo sembrado
    (`pk1`, `pk3`), no de norma: se corrigen en sitio. */
export const PLANTILLAS = {
  recamara: { nombre: 'recámara', items: { contacto: 4, 'salida-luz': 2 } },
  sala:     { nombre: 'sala', items: { contacto: 4, 'salida-luz': 2 } },
  comedor:  { nombre: 'comedor', items: { contacto: 2, 'salida-luz': 1 } },
  cocina:   { nombre: 'cocina', items: { 'contacto-agua': 3, 'salida-luz': 2 } },
  bano:     { nombre: 'baño', items: { 'contacto-agua': 1, 'salida-luz': 1 } },
  pasillo:  { nombre: 'pasillo', items: { escalera: 1, 'salida-luz': 1 } },
  estudio:  { nombre: 'estudio', items: { contacto: 4, 'salida-luz': 1 } },
  jardin:   { nombre: 'jardín', items: { 'contacto-agua': 1, 'salida-luz': 1 } },
  patio:    { nombre: 'patio', items: { 'contacto-agua': 1, 'salida-luz': 1 } },
  cochera:  { nombre: 'cochera', items: { contacto: 1, 'salida-luz': 1 } }
}
const plantillaDe = nombre => {
  const n = (nombre || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  return Object.entries(PLANTILLAS).find(([k]) => n.includes(k))?.[1] || null
}

export const idContador = (recinto, tipo, estado) => `${recinto}__${tipo}__${estado}`
export const idAparato = (recinto, aparato, estado) => `${recinto}__aparato-${aparato}__${estado}`

/** Resumen en una línea para la lista de recintos: «4 contactos · 2 luces · 1 hallazgo». */
export function resumenObjetos(objetos, recinto) {
  const mios = Object.values(objetos || {}).filter(o => o.recinto === recinto)
  if (!mios.length) return ''
  /* Lo que HAY y lo que se PROPONE se dicen por separado: sumarlos («7 contactos»)
     engaña justo en la lista que el vendedor usa para saber por dónde va. */
  const suma = (estado, tipos) => mios.filter(o => o.estado === estado && tipos.includes(o.tipo)).reduce((a, o) => a + (o.q || 0), 0)
  const frase = estado => {
    const c = suma(estado, ['contacto', 'contacto-agua'])
    const l = suma(estado, ['salida-luz', 'escalera'])
    const a = mios.filter(o => o.estado === estado && o.tipo === 'aparato').length
    const p = []
    if (c) p.push(`${c} ${c === 1 ? 'contacto' : 'contactos'}`)
    if (l) p.push(`${l} ${l === 1 ? 'luz' : 'luces'}`)
    if (a) p.push(`${a} ${a === 1 ? 'aparato' : 'aparatos'}`)
    return p.join(', ')
  }
  const partes = []
  const hay = frase('existente');   if (hay) partes.push(`hay ${hay}`)
  const propone = frase('propuesto'); if (propone) partes.push(`propone ${propone}`)
  const h = mios.filter(o => o.tipo === 'hallazgo').length
  if (h) partes.push(`${h} ${h === 1 ? 'hallazgo' : 'hallazgos'}`)
  return partes.join(' · ')
}

const ahora = () => new Date().toISOString()

/** Miniatura que se carga por id desde `fotos`. */
function Foto({ id, alt }) {
  const [src, setSrc] = useState(null)
  useEffect(() => { let vivo = true; store.obtenerFoto(id).then(d => { if (vivo) setSrc(d) }); return () => { vivo = false } }, [id])
  return src ? <img src={src} alt={alt} /> : <span className="ph" style={{ width: 72, height: 72 }} />
}

/**
 * Props:
 *   recinto      id del recinto (= id de la habitación)
 *   nombre       nombre del recinto (para la plantilla)
 *   objetos      cot.objetos (mapa completo)
 *   onObjeto(id, valor | null)   escribe o borra un objeto (el padre hace guardarParcial / borrarCampo)
 *   fotos        cot.fotosRecinto?.[recinto] || []
 *   onFotos(lista)
 *   nota         cot.notasRecinto?.[recinto] || ''
 *   onNota(texto)
 *   cotId        para etiquetar las fotos
 */
export default function PaletaElectrica({ recinto, nombre, objetos = {}, onObjeto, fotos = [], onFotos, nota = '', onNota, cotId }) {
  const [estado, setEstado] = useState('existente')
  const [eligiendoAparato, setEligiendoAparato] = useState(false)
  const [hallazgoAbierto, setHallazgoAbierto] = useState(null)   // id del hallazgo al que se le está tomando foto
  const [notaLocal, setNotaLocal] = useState(nota)
  useEffect(() => { setNotaLocal(nota) }, [nota])

  const mios = Object.entries(objetos).filter(([, o]) => o.recinto === recinto)
  const cuenta = tipo => objetos[idContador(recinto, tipo, estado)]?.q || 0
  const poner = (tipo, q) => {
    const id = idContador(recinto, tipo, estado)
    if (q <= 0) { onObjeto(id, null); return }
    onObjeto(id, { id, recinto, rubro: 'electrico', tipo, estado, q, sku: TIPOS_ELECTRICO.find(t => t.id === tipo)?.sku || null, capturadoEn: ahora() })
  }
  const aparatos = mios.filter(([, o]) => o.tipo === 'aparato' && o.estado === estado)
  const ponerAparato = (ap, q) => {
    const id = idAparato(recinto, ap.id, estado)
    if (q <= 0) { onObjeto(id, null); return }
    onObjeto(id, { id, recinto, rubro: 'electrico', tipo: 'aparato', estado, q, detalle: ap.id, nombre: ap.nombre, sku: ap.dedicado ? 'CTO-DED' : null, capturadoEn: ahora() })
  }
  const hallazgos = mios.filter(([, o]) => o.tipo === 'hallazgo')
  const abrirHallazgo = detalle => {
    const id = `${recinto}__hallazgo-${store.nuevoId()}`
    onObjeto(id, { id, recinto, rubro: 'electrico', tipo: 'hallazgo', estado: 'existente', q: 1, detalle, fotos: [], capturadoEn: ahora() })
    setHallazgoAbierto(id)
  }
  const subirEvidencia = async (archivo, lado = 1200, calidad = 0.8) => {
    const grande = await store.comprimirImagen(archivo, lado, calidad)
    return store.subirFoto(grande, { cotizacion: cotId, recinto, clase: lado >= 1000 ? 'evidencia' : 'miniatura', tomadaEn: ahora() })
  }

  const plantilla = plantillaDe(nombre)
  const hayPropuesto = mios.some(([, o]) => o.estado === 'propuesto' && o.tipo !== 'hallazgo')
  const aplicarPlantilla = () => {
    for (const [tipo, q] of Object.entries(plantilla.items)) {
      const id = idContador(recinto, tipo, 'propuesto')
      onObjeto(id, { id, recinto, rubro: 'electrico', tipo, estado: 'propuesto', q, sku: TIPOS_ELECTRICO.find(t => t.id === tipo)?.sku || null, capturadoEn: ahora() })
    }
    setEstado('propuesto')
  }

  return (
    <div className="paleta">
      {/* Existente / Propuesto: un conmutador, no dos pantallas. */}
      <div className="tabs" style={{ marginBottom: 'var(--e4)' }}>
        <button type="button" className={`tb ${estado === 'existente' ? 'on' : ''}`} onClick={() => setEstado('existente')}>Lo que hay</button>
        <button type="button" className={`tb ${estado === 'propuesto' ? 'on' : ''}`} onClick={() => setEstado('propuesto')}>Lo que se propone</button>
      </div>

      {estado === 'propuesto' && plantilla && !hayPropuesto && (
        <button type="button" className="ib w" style={{ marginBottom: 'var(--e5)' }} onClick={aplicarPlantilla}>
          Proponer lo típico de una {plantilla.nombre}
        </button>
      )}

      {/* Contadores: lo que no necesita posición se cuenta a toques. */}
      {TIPOS_ELECTRICO.map(t => {
        const q = cuenta(t.id)
        return (
          <div className="paleta-tipo" key={t.id}>
            <div className="grow">
              <div className="t">{t.nombre}</div>
              {estado === 'propuesto' && t.sku && <div className="s">Se cotiza como {t.sku}</div>}
            </div>
            <div className="qty">
              <button type="button" aria-label={`Quitar un ${t.nombre.toLowerCase()}`} onClick={() => poner(t.id, q - 1)} disabled={!q}>−</button>
              <span>{q}</span>
              <button type="button" aria-label={`Agregar un ${t.nombre.toLowerCase()}`} onClick={() => poner(t.id, q + 1)}>+</button>
            </div>
          </div>
        )
      })}

      {/* Aparatos que consumen: de la lista del motor eléctrico, con su zona. */}
      <div className="paleta-tipo">
        <div className="grow">
          <div className="t">Aparatos que consumen</div>
          <div className="s">{aparatos.length ? aparatos.map(([, o]) => `${o.nombre}${o.q > 1 ? ` ×${o.q}` : ''}`).join(' · ') : 'Refrigerador, minisplit, bomba… lo que jala de este recinto.'}</div>
        </div>
        <button type="button" className="ib sm" onClick={() => setEligiendoAparato(v => !v)}>{eligiendoAparato ? 'Listo' : 'Elegir'}</button>
      </div>
      {eligiendoAparato && (
        <div className="fila-pills" style={{ marginBottom: 'var(--e5)' }}>
          {APARATOS.map(ap => {
            const q = objetos[idAparato(recinto, ap.id, estado)]?.q || 0
            return (
              <button type="button" key={ap.id} className={`et ${q ? 'on1' : ''}`} style={{ flex: '1 1 45%' }}
                onClick={() => ponerAparato(ap, q ? 0 : 1)}>{ap.nombre}{q > 1 ? ` ×${q}` : ''}</button>
            )
          })}
        </div>
      )}

      {/* Hallazgos: lo que se ve, se huele o se toca — siempre con foto. */}
      <div className="sec" style={{ marginTop: 'var(--e6)' }}>Hallazgos</div>
      <div className="fila-pills">
        {HALLAZGOS.map(h => (
          <button type="button" key={h} className="ib sm dz" onClick={() => abrirHallazgo(h)}>{h}</button>
        ))}
      </div>
      {hallazgos.map(([id, h]) => (
        <div className="warn" key={id} style={{ marginTop: 0 }}>
          <b>{h.detalle}</b>{!(h.fotos || []).length && ' — falta la foto'}
          <div className="fotos" style={{ marginTop: 10, marginBottom: 0 }}>
            {(h.fotos || []).map(f => <span className="foto" key={f}><Foto id={f} alt={h.detalle} /></span>)}
            {(h.fotos || []).length < 4 && (
              <label className="add" aria-label="Tomar foto del hallazgo">+
                <input type="file" accept="image/*" capture="environment" hidden onChange={async e => {
                  const archivo = e.target.files?.[0]; if (!archivo) return
                  const idFoto = await subirEvidencia(archivo)
                  onObjeto(id, { ...h, fotos: [...(h.fotos || []), idFoto] })
                  e.target.value = ''
                }} />
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" className="ib sm" onClick={() => onObjeto(id, null)}>Quitar</button>
          </div>
        </div>
      ))}

      {/* Fotos del recinto y nota dictada, en la zona del pulgar. */}
      <div className="sec">Fotos y nota del recinto</div>
      <div className="fotos">
        {fotos.map(f => <span className="foto" key={f}><Foto id={f} alt="Foto del recinto" /></span>)}
        {fotos.length < 6 && (
          <label className="add" aria-label="Tomar foto del recinto">+
            <input type="file" accept="image/*" capture="environment" hidden onChange={async e => {
              const archivo = e.target.files?.[0]; if (!archivo) return
              const idFoto = await subirEvidencia(archivo, 420, 0.72)
              onFotos([...fotos, idFoto])
              e.target.value = ''
            }} />
          </label>
        )}
      </div>
      <label className="f">
        <span>Nota del recinto — dicta con el micrófono del teclado</span>
        <textarea value={notaLocal} style={{ minHeight: 70 }} placeholder="Lo que el cliente dijo, lo que viste…"
          onChange={e => setNotaLocal(e.target.value)} onBlur={() => { if (notaLocal !== nota) onNota(notaLocal.trim()) }} />
      </label>
    </div>
  )
}
